import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";
import Seo from "@/components/Seo";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section } from "@/components/layout/Section";
import { cn } from "@/lib/utils";

const Cell = ({ value }) =>
  value === false || value == null ? (
    <Minus size={15} className="mx-auto text-brand-mist/40" aria-label="Not included" />
  ) : value === true ? (
    <Check size={15} className="mx-auto text-brand-emerald" aria-label="Included" />
  ) : (
    <span className="text-xs text-brand-snow/90">{value}</span>
  );

const PricingLayout = ({ seo, eyebrow, title, intro, packages, matrix, crossLink, testid }) => (
  <>
    <Seo {...seo} />
    <Section testid={testid} className="pt-32 sm:pt-40">
      <Container className="space-y-14">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" data-testid={`${testid}-breadcrumb`} className="text-xs text-brand-mist">
          <Link to="/" className="hover:text-brand-emerald">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-brand-snow">{title}</span>
        </nav>

        {/* Hero */}
        <div className="max-w-3xl space-y-5" data-testid={`${testid}-heading`}>
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-brand-snow sm:text-4xl lg:text-5xl">
            {title}
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-brand-mist">{intro}</p>
        </div>

        {/* Package cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4" data-testid={`${testid}-cards`}>
          {packages.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              data-testid={`${testid}-card-${p.key}`}
              className={cn(
                "flex flex-col rounded-2xl border p-6",
                p.highlight
                  ? "border-brand-emerald/50 bg-brand-charcoal shadow-lg shadow-brand-emerald/10"
                  : "border-brand-mist/15 bg-brand-charcoal/60"
              )}
            >
              {p.highlight && (
                <span className="mb-3 w-fit rounded-full bg-brand-emerald/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-emerald">
                  Most popular
                </span>
              )}
              <h2 className="font-display text-xl font-bold text-brand-snow">{p.name}</h2>
              <p className="mt-2 text-sm leading-relaxed text-brand-mist">{p.blurb}</p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-brand-snow/90">
                    <Check size={14} className="mt-0.5 shrink-0 text-brand-emerald" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <SiteButton to={`/contact?source=${p.key}-package`} testid={`${testid}-cta-${p.key}`} className="w-full">
                  Request a Quote
                </SiteButton>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Feature comparison */}
        <div className="overflow-x-auto rounded-2xl border border-brand-mist/15" data-testid={`${testid}-comparison`}>
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className="border-b border-brand-mist/15 bg-brand-black/40">
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-brand-mist">Feature</th>
                {packages.map((p) => (
                  <th key={p.key} className="px-5 py-4 text-center font-display text-sm font-bold text-brand-snow">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map(([feature, values], i) => (
                <tr key={feature} className={cn("border-b border-brand-mist/10", i % 2 && "bg-brand-black/20")}>
                  <td className="px-5 py-3.5 text-sm text-brand-snow/90">{feature}</td>
                  {values.map((v, j) => (
                    <td key={j} className="px-5 py-3.5 text-center">
                      <Cell value={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cross-link + CTA */}
        <div className="rounded-2xl border border-brand-emerald/20 bg-brand-charcoal p-10 text-center" data-testid={`${testid}-cta`}>
          <h2 className="mx-auto max-w-xl font-display text-2xl font-bold text-brand-snow sm:text-3xl">
            Not sure which package fits?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-brand-mist">{crossLink.prompt}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <SiteButton to={`/contact?source=${testid}`} testid={`${testid}-cta-quote`} size="lg">Request a Quote</SiteButton>
            <SiteButton to={crossLink.href} testid={`${testid}-cta-cross`} variant="ghost" size="lg">
              {crossLink.label}
            </SiteButton>
          </div>
        </div>
      </Container>
    </Section>
  </>
);

export default PricingLayout;
